// src/Components/ProductSectionsBuilder.js

import { Stack, TextField, IconButton, Card, Typography, Button, Autocomplete } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

export function ProductSectionsBuilder({ value = [], onChange, handlePreview, products = [] }) {
  const max = 10;
  const MAX_TOTAL_PRODUCTS = 30;

  const currentTotalProducts = value.reduce((sum, section) => sum + (section.product_ids?.length || 0), 0);

  const addSection = () => {
    if ((value?.length || 0) >= max) return;
    const next = [...(value || []), { title: "", product_ids: [] }];
    onChange(next);
    handlePreview && handlePreview();
  };

  const updateSection = (i, patch) => {
    const next = [...value];
    next[i] = { ...next[i], ...patch };
    onChange(next);
    handlePreview && handlePreview();
  };

  const removeSection = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange(next);
    handlePreview && handlePreview();
  };

  const addProductId = (i) => {
    if (currentTotalProducts >= MAX_TOTAL_PRODUCTS) return;

    const next = [...value];
    next[i].product_ids = [...(next[i].product_ids || []), ""];
    onChange(next);
    handlePreview && handlePreview();
  };

  const updateProductId = (i, pi, val) => {
    const next = [...value];
    next[i].product_ids[pi] = val;
    onChange(next);
    handlePreview && handlePreview();
  };

  const removeProductId = (i, pi) => {
    const next = [...value];
    next[i].product_ids.splice(pi, 1);
    onChange(next);
    handlePreview && handlePreview();
  };

  return (
    <Stack gap={1} sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Product sections({value.length}/{max})</Typography>
        <IconButton onClick={addSection} disabled={(value?.length || 0) >= max} sx={{ color: "#25d366" }}>
          <AddCircleIcon />
        </IconButton>
      </Stack>

      {value?.map((section, i) => (
        <Card key={i} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Section {i + 1}</Typography>
            <IconButton onClick={() => removeSection(i)} sx={{ color: "#df1d5a" }}>
              <RemoveCircleIcon />
            </IconButton>
          </Stack>

          <Stack gap={1} sx={{ mt: 1 }}>
            <TextField
              label="Section title"
              size="small"
              value={section.title || ""}
              onChange={(e) => updateSection(i, { title: e.target.value })}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="caption">Product IDs</Typography>
              <Button onClick={() => addProductId(i)} size="small" disabled={currentTotalProducts >= MAX_TOTAL_PRODUCTS}>Add product</Button>
            </Stack>
            {(section.product_ids || []).map((pid, pi) => {
              const prod = products.find((p) => p.retailer_id === pid) || null;

              return (
                <Stack key={pi} direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                  <Autocomplete
                    value={prod}
                    options={products}
                    getOptionLabel={(opt) => [opt?.name, opt?.retailer_id].filter(Boolean).join(" — ")}
                    onChange={(e, data) => {
                      const next = [...value];
                      next[i].product_ids[pi] = data?.retailer_id || "";
                      onChange(next);
                      handlePreview && handlePreview();
                    }}
                    renderInput={(params) => <TextField {...params} label="Product" size="small" />}
                    sx={{ minWidth: 280 }}
                  />

                  <IconButton onClick={() => removeProductId(i, pi)} sx={{ color: "#df1d5a" }}>
                    <RemoveCircleIcon />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        </Card>
      ))}
      {currentTotalProducts >= MAX_TOTAL_PRODUCTS && (
        <Typography variant="caption" color="error">
          Maximum limit of 30 products reached across all sections.
        </Typography>
      )}
    </Stack>
  );
}